import { useEffect, useRef } from "react";
import { useConnectionState, useZero } from "@rocicorp/zero/react";
import { addSentryBreadcrumb } from "@/lib/sentry";
import { capturePostHogEvent } from "@/lib/posthog";

/**
 * Auto-recovers from Zero's terminal `error` connection state — forever,
 * with no user action ever required.
 *
 * Zero enters `error` when the mutate OR query endpoint returns a
 * non-200/401/403 (e.g. a 502 while `api-us` is being recreated during a
 * deploy). Zero deliberately does NOT auto-retry `error` ("unlikely to
 * resolve by retrying, so Zero doesn't try") — recovery is the app's job,
 * via `zero.connection.connect()`. Today the Ano client only recovers
 * `needs-auth` (see `zero-auth-recovery.tsx`), so an `error` leaves the
 * user stuck reads-only (writes rejected) until they reload. This
 * component closes that gap: a client knocked into `error` self-heals
 * within seconds, on every future deploy, with zero clicks.
 *
 * ## Cap the INTERVAL, never the attempt count
 *
 * This is a chat app — there is no acceptable "click to reconnect" state.
 * So we retry indefinitely. The only thing we bound is the *interval*:
 * exponential backoff with full jitter, plateauing at ~30s. One request
 * per ~30s per client is not a storm (Zero's own `disconnected` retry is
 * every 5s), and a deploy-502 resolves on the first attempt or two. A
 * genuinely-broken backend just gets gently probed until it returns —
 * which is exactly the self-healing behaviour a chat app must have. (A
 * client that needs *fresh code* is handled separately by Zero's
 * `onUpdateNeeded` → update/reload path in `AuthGate`, not here.)
 *
 * ## Why a bare `connect()` (no token refresh)
 *
 * `zero-auth-recovery.tsx` / `zero-visibility-refresh.tsx` mint a fresh
 * token then `connect({ auth })` — correct for THEM, because they handle
 * auth failures. Here the trigger is a 502/network error, NOT an auth
 * failure (a 401/403 routes to `needs-auth`, which we never touch). The
 * stored token is therefore still valid, and crucially:
 * `refreshZeroSessionToken` hits the API — the SAME server that is 502-ing
 * during the deploy window — so refreshing would block recovery during the
 * exact window we are recovering from. A bare `connect()` reuses the valid
 * token and works the instant `api-us` is back. If the token has since
 * expired, `connect()` → 401 → `needs-auth` → `zero-auth-recovery`
 * refreshes it (self-corrects).
 *
 * ## Why a self-driving timer (not an effect keyed on state)
 *
 * Zero's connection manager DEDUPES `error`: once in `error` it emits no
 * further state, so an effect keyed on `connectionState` would fire once
 * and never re-arm. We drive retries from our own `setTimeout`: each
 * attempt calls `connect()`, which transitions `error → connecting →
 * error` (each a fresh emission) and re-runs the effect, which schedules
 * the next attempt with a longer, jittered delay.
 *
 * ## Jitter
 *
 * Every connected client hits the deploy's error window at roughly the
 * same instant, so a fixed backoff would reconnect the whole fleet in
 * lockstep and spike `api-us` as it recovers. Full jitter
 * (`random(0, ceil)`) spreads reconnects out.
 *
 * ## Telemetry
 *
 * There was no prod signal for how often clients hit `error` on a deploy.
 * We emit a PostHog event on error-entry (counts the episodes — the
 * deploy-impact metric) and on recovery (with the attempt count — how fast
 * it self-healed). PostHog is the right tool here: it's volume-tolerant and
 * fires independent of any later exception, unlike a Sentry breadcrumb
 * (which only ships if an exception is captured later in the session — the
 * self-healing case usually captures none). We ALSO drop Sentry breadcrumbs
 * for crash-context when an unrelated exception does fire.
 *
 * Status is surfaced passively by `use-connection-status-toasts`
 * ("Connection lost — reconnecting…" → "Back online"); this component
 * renders nothing. Must mount inside `<ZeroProvider>`.
 */

const BASE_DELAY_MS = 1_000;
const MAX_DELAY_MS = 30_000;
// Exponent cap so `2 ** attempt` can't overflow on a long-running outage;
// 2**5 * 1s = 32s already exceeds the 30s ceiling, so 6 is plenty.
const MAX_BACKOFF_EXPONENT = 6;
// While the tab is hidden, don't burn reconnect attempts against a
// possibly-broken endpoint — re-check cheaply until it's visible again.
// NOTE: Zero's `hiddenTabDisconnectDelay` only disconnects a CONNECTED
// tab, not one already in `error`, so it won't move us out of `error` on
// its own. This re-check is the backstop; the primary resume on tab-return
// is `ZeroVisibilityRefresh`'s `connect()`, whose state change re-drives
// this effect.
const RECHECK_WHILE_HIDDEN_MS = 30_000;

/** Full-jitter exponential backoff ceiling for the given attempt index. */
function jitteredDelayMs(attempt: number): number {
  const exponent = Math.min(attempt, MAX_BACKOFF_EXPONENT);
  const ceil = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** exponent);
  return Math.random() * ceil;
}

export function ZeroErrorRecovery() {
  const zero = useZero();
  const connectionState = useConnectionState();

  const attemptsRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks whether we're inside an error "episode" so we emit exactly one
  // breadcrumb on entry and one on recovery (not one per connecting bounce).
  const inEpisodeRef = useRef(false);

  useEffect(() => {
    const name = connectionState.name;

    const clearTimer = () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    if (name === "connected") {
      clearTimer();
      if (inEpisodeRef.current) {
        addSentryBreadcrumb({
          category: "zero-connection",
          message: "recovered from error state",
          level: "info",
          data: { attempts: attemptsRef.current },
        });
        capturePostHogEvent("zero_connection_recovered", {
          attempts: attemptsRef.current,
        });
      }
      attemptsRef.current = 0;
      inEpisodeRef.current = false;
      return;
    }

    if (name !== "error") {
      // connecting / disconnected / needs-auth / closed — not ours. Zero
      // self-heals `disconnected`/`connecting`; `needs-auth` is owned by
      // ZeroAuthRecovery. Stand down (but keep episode state — an error
      // episode can bounce through `connecting` between attempts).
      clearTimer();
      return;
    }

    // name === "error"
    if (!inEpisodeRef.current) {
      inEpisodeRef.current = true;
      addSentryBreadcrumb({
        category: "zero-connection",
        message: "entered error state",
        level: "warning",
        data: { reason: connectionState.reason },
      });
      capturePostHogEvent("zero_connection_error", {
        reason: connectionState.reason,
      });
    }

    if (timerRef.current !== null) return; // already scheduled

    const runAttempt = () => {
      timerRef.current = null;

      // Don't reconnect a backgrounded tab — re-check cheaply instead.
      if (typeof document !== "undefined" && document.hidden) {
        timerRef.current = setTimeout(runAttempt, RECHECK_WHILE_HIDDEN_MS);
        return;
      }

      attemptsRef.current += 1;
      // The resulting state change (connecting → connected | error) re-runs
      // the effect, which schedules the next attempt if still erroring.
      void zero.connection.connect().catch(() => {});
    };

    timerRef.current = setTimeout(
      runAttempt,
      jitteredDelayMs(attemptsRef.current),
    );

    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [connectionState, zero]);

  return null;
}
